import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import {
  CONSOLE_CITY_ONBOARDING_CHECKLIST_DOC_URL,
  CONSOLE_CITY_ONBOARDING_SOP_DOC_URL,
  CONSOLE_INTL_ONBOARDING_DOC_URL,
  getApiErrorMessage,
  getConsoleCityUrl,
} from "../../utils/consoleUtils";
import { ErrorHandler } from "../../utils/errorHandler";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { GooglePlacesCityInput, isGooglePlacesConfigured } from "../GooglePlacesAddressInput";
import "../../styles/console.css";

const STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "seeding", label: "Seeding" },
  { value: "runnable", label: "Runnable" },
  { value: "stable", label: "Stable" },
];

const emptyForm = () => ({
  name: "",
  region_label: "",
  status: "planned",
  order: 0,
  notes: "",
});

export default function CitiesView() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { data, loading, error, refetch } = useConsoleFetch(
    () => consoleApi.cities.list({ limit: 100 }).then((r) => (Array.isArray(r) ? r : [])),
    [],
    { refetchOnWindowFocus: true }
  );

  const cities = data ?? [];

  const openCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  }, []);

  const openEdit = useCallback((city) => {
    setEditingId(city.id);
    setForm({
      name: city.name ?? "",
      region_label: city.region_label ?? "",
      status: city.status ?? "planned",
      order: typeof city.order === "number" ? city.order : 0,
      notes: city.notes ?? "",
    });
    setShowModal(true);
  }, []);

  const closeModal = useCallback((force = false) => {
    if (!force && submitting) return;
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm());
  }, [submitting]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const name = (form.name ?? "").trim();
      if (!name) {
        toast.error("Name is required.");
        return;
      }
      const order = Number(form.order);
      const body = {
        name,
        region_label: (form.region_label ?? "").trim() || undefined,
        status: form.status || "planned",
        order: Number.isFinite(order) && order >= 0 ? order : 0,
        notes: (form.notes ?? "").trim() || undefined,
      };
      setSubmitting(true);
      try {
        if (editingId) {
          await consoleApi.cities.update(editingId, body);
          toast.success("City updated.");
          closeModal(true);
          refetch();
        } else {
          const created = await consoleApi.cities.create(body);
          const newId = created?.id;
          closeModal(true);
          refetch();
          toast.success("City created. Opening Clubs for this city…");
          if (newId) {
            navigate(getConsoleCityUrl("clubs", newId));
          }
        }
      } catch (err) {
        ErrorHandler.logError("CitiesView", err, { editingId, action: editingId ? "update" : "create" });
        toast.error(getApiErrorMessage(err, editingId ? "Failed to update city" : "Failed to create city"));
      } finally {
        setSubmitting(false);
      }
    },
    [form, editingId, closeModal, refetch, navigate]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      setDeleting(true);
      try {
        await consoleApi.cities.delete(id);
        toast.success("City deleted.");
        setDeleteId(null);
        refetch();
      } catch (err) {
        ErrorHandler.logError("CitiesView", err, { cityId: id, action: "delete" });
        toast.error(getApiErrorMessage(err, "Failed to delete city"));
      } finally {
        setDeleting(false);
      }
    },
    [refetch]
  );

  const copyId = useCallback((id) => {
    if (!id || !navigator.clipboard) return;
    navigator.clipboard.writeText(id).then(
      () => toast.success("City ID copied"),
      () => toast.error("Could not copy")
    );
  }, []);

  return (
    <ConsoleViewShell loading={loading && !data} error={error} loadingLabel="Loading cities…">
      <div className="console-main-inner">
        <ConsolePageHeader
          label="Network"
          title="Cities"
          sub="Create and manage city records for ?city= UUID links and club onboarding. Network cards for new markets still appear once clubs exist."
        />
        <div className="flex flex-wrap gap-2 mb-4">
          <Button type="button" className="tb-btn primary" onClick={openCreate}>
            Add city
          </Button>
          <Link
            to="/console/onboarding-sop"
            className="tb-btn inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm"
          >
            City onboarding SOP
          </Link>
          <Link to="/console" className="tb-btn inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm">
            ← Network overview
          </Link>
        </div>

        <p className="text-sm text-[var(--console-muted)] mb-4 max-w-3xl">
          Start with the{" "}
          <a
            href={CONSOLE_CITY_ONBOARDING_SOP_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--console-accent)] hover:underline"
          >
            city onboarding SOP
          </a>{" "}
          and{" "}
          <a
            href={CONSOLE_CITY_ONBOARDING_CHECKLIST_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--console-accent)] hover:underline"
          >
            completion checklist
          </a>
          . Then use the console city tabs to execute. For international city naming and timezone setup, see the{" "}
          <a
            href={CONSOLE_INTL_ONBOARDING_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--console-accent)] hover:underline"
          >
            international onboarding guide
          </a>
          . Sources:{" "}
          <code className="text-xs">docs/city-onboarding-sop.md</code>,{" "}
          <code className="text-xs">docs/city-onboarding-checklist.md</code>,{" "}
          <code className="text-xs">docs/console-international-onboarding.md</code>.
        </p>

        <div className="console-table-wrap overflow-x-auto border border-[var(--console-border)] rounded-lg">
          <table className="w-full text-sm text-left">
            <thead className="bg-[var(--console-surface)] border-b border-[var(--console-border)]">
              <tr>
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Region</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Order</th>
                <th className="p-3 font-medium">ID</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cities.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="p-6 text-[var(--console-muted)]">
                    No cities in the database yet. Add one to get a stable UUID for console links.
                  </td>
                </tr>
              )}
              {cities.map((c) => (
                <tr key={c.id} className="border-b border-[var(--console-border)] hover:bg-[var(--console-surface)]/50">
                  <td className="p-3 font-medium text-[var(--console-ink)]">{c.name}</td>
                  <td className="p-3 text-[var(--console-muted)]">{c.region_label ?? "—"}</td>
                  <td className="p-3">{c.status ?? "—"}</td>
                  <td className="p-3">{typeof c.order === "number" ? c.order : "—"}</td>
                  <td className="p-3">
                    <code className="text-xs break-all">{c.id}</code>
                    <button
                      type="button"
                      className="ml-2 text-xs text-[var(--console-accent)] hover:underline"
                      onClick={() => copyId(c.id)}
                    >
                      Copy
                    </button>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button type="button" variant="outline" size="sm" className="mr-1" onClick={() => openEdit(c)}>
                      Edit
                    </Button>
                    <Link
                      to={getConsoleCityUrl("clubs", c.id)}
                      className="inline-flex items-center justify-center rounded-md border border-[var(--console-border)] bg-transparent px-3 py-1 text-sm hover:bg-[var(--console-surface)]"
                    >
                      Open clubs
                    </Link>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-1 text-destructive border-destructive/50"
                      onClick={() => setDeleteId(c.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="console-page max-w-md bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit city" : "Add city"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-4 py-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="city-name">
                Name <span className="text-[var(--console-red)]">*</span>
              </label>
              {isGooglePlacesConfigured() ? (
                <>
                  <GooglePlacesCityInput
                    id="city-name"
                    value={form.name}
                    onChange={(name) => setForm((f) => ({ ...f, name }))}
                    onPlaceConfirmed={(data) => {
                      const name =
                        (data.displayName || "").trim() ||
                        (data.cityName || "").trim() ||
                        (data.formattedAddress || "").split(",")[0]?.trim() ||
                        "";
                      setForm((f) => ({
                        ...f,
                        name: name || f.name,
                        region_label: (data.regionLabel || "").trim() || f.region_label,
                      }));
                    }}
                    placeholder="Search Google for a city, then pick a result"
                    aria-label="City name (Google Places)"
                    className="border-[var(--console-border)] bg-[var(--console-surface)]"
                  />
                  <p className="text-xs text-[var(--console-muted)]">
                    Pick a suggestion to fill the name with city and country from Google. Region fills when available;
                    edit either field if you need a shorter slug for clubs (see international onboarding guide).
                  </p>
                </>
              ) : (
                <Input
                  id="city-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Paris"
                  required
                  className="border-[var(--console-border)] bg-[var(--console-surface)]"
                />
              )}
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="city-region">
                Region label
              </label>
              <Input
                id="city-region"
                value={form.region_label}
                onChange={(e) => setForm((f) => ({ ...f, region_label: e.target.value }))}
                placeholder="e.g. Île-de-France"
                className="border-[var(--console-border)] bg-[var(--console-surface)]"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="city-status">
                Status
              </label>
              <select
                id="city-status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="city-order">
                Sort order
              </label>
              <Input
                id="city-order"
                type="number"
                min={0}
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                className="border-[var(--console-border)] bg-[var(--console-surface)]"
              />
              <p className="text-xs text-[var(--console-muted)]">
                Controls the order of rows on this Cities page: lower numbers first, then alphabetical by name when tied.
                Does not affect club or network behavior.
              </p>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium" htmlFor="city-notes">
                Notes
              </label>
              <Textarea
                id="city-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
                className="border-[var(--console-border)] bg-[var(--console-surface)] resize-none"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="tb-btn primary" disabled={submitting}>
                {submitting ? "Saving…" : editingId ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && !deleting && setDeleteId(null)}>
        <DialogContent className="console-page max-w-sm bg-[var(--console-surface)] border-[var(--console-border)]">
          <DialogHeader>
            <DialogTitle>Delete city?</DialogTitle>
            <p className="text-sm text-[var(--console-muted)]">
              This removes the city record only. Clubs keep their data; unlink or update <code className="text-xs">city_id</code> if needed.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" disabled={deleting} onClick={() => handleDelete(deleteId)}>
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConsoleViewShell>
  );
}
